import { useMemo } from 'react';
import {
    Box,
    TableBody,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery
} from '@mui/material';

import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import { calculateSecondsToHoursAndMinutes } from '@libs/dateTime';

import {
    TimesheetsNoDataFound,
    TimesheetsUser,
    DaySquares,
    WeekdayStrip,
    AttendanceSummaryChips
} from '../../../components';
import {
    buildMonthDaySheet,
    summarizeDays
} from 'timeAndAttendance/statusTokens';
import {
    timesheetSummaryHeaders,
    timesheetSummaryHeaderFullNames
} from '../../common';
import { useMonthlyTimesheetsState } from '../useMonthlyTimesheets';

const SUMMARY_KEYS = Object.keys(timesheetSummaryHeaders);

const lateBreakdown = (summary) =>
    `On site late ${summary.lateOnsite || 0} · Remote late ${
        summary.lateRemote || 0
    }`;

/** First column stays put while the rest scrolls. */
const stickySx = (zIndex) => ({
    position: 'sticky',
    left: 0,
    zIndex,
    backgroundColor: (theme) => theme.palette.background.default
});

const hoursOf = (user) =>
    `${calculateSecondsToHoursAndMinutes(user.totalDuration || 0)}/` +
    `${calculateSecondsToHoursAndMinutes(user.totalShiftDuration || 0)}`;

const useUserMonth = (user, yearMonth) => {
    const daySheet = useMemo(
        () =>
            buildMonthDaySheet(user.entries || [], yearMonth, {
                joiningDate: user.joiningDate,
                resignationDate: user.resignationDate
            }),
        [user, yearMonth]
    );
    const summary = useMemo(() => summarizeDays(daySheet), [daySheet]);
    return { daySheet, summary };
};

/* ------------------------------------------------------------------ */
/* Desktop row (common table components)                               */
/* ------------------------------------------------------------------ */
const UserRow = ({ user, yearMonth }) => {
    const { daySheet, summary } = useUserMonth(user, yearMonth);

    return (
        <TableRow>
            <TableCellContainer
                sx={{
                    ...stickySx(1),
                    pl: 2,
                    minWidth: 230,
                    maxWidth: 260
                }}
            >
                <TimesheetsUser
                    name={user.email}
                    avatarUrl={user.avatarUrl}
                    sx={{
                        minWidth: 0,
                        '& .MuiTypography-root': { minWidth: 0, flex: 1 }
                    }}
                />
            </TableCellContainer>

            {SUMMARY_KEYS.map((key) => (
                <TableCellContainer key={key} sx={{ textAlign: 'center' }}>
                    {key === 'arrivedLate' ? (
                        <Tooltip title={lateBreakdown(summary)}>
                            <span style={{ cursor: 'help' }}>
                                {summary[key] || 0}
                            </span>
                        </Tooltip>
                    ) : (
                        summary[key] || 0
                    )}
                </TableCellContainer>
            ))}

            <TableCellContainer sx={{ py: 1 }}>
                <DaySquares daySheet={daySheet} yearMonth={yearMonth} />
            </TableCellContainer>

            <TableCellContainer
                sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}
            >
                {hoursOf(user)}
            </TableCellContainer>
        </TableRow>
    );
};

/* ------------------------------------------------------------------ */
/* Mobile card                                                         */
/* ------------------------------------------------------------------ */
const UserCard = ({ user, yearMonth }) => {
    const { daySheet, summary } = useUserMonth(user, yearMonth);

    return (
        <Box
            sx={{
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                backgroundColor: (theme) => theme.palette.background.paper
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1
                }}
            >
                <TimesheetsUser
                    name={user.email}
                    avatarUrl={user.avatarUrl}
                    sx={{
                        minWidth: 0,
                        flex: 1,
                        '& .MuiTypography-root': { minWidth: 0, flex: 1 }
                    }}
                />
                <Typography
                    variant='caption'
                    sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                    {hoursOf(user)}
                </Typography>
            </Box>

            <AttendanceSummaryChips summary={summary} sx={{ mt: 1.5 }} />

            <Box sx={{ overflowX: 'auto', mt: 1.5, pb: 0.5 }}>
                <Box sx={{ width: 'max-content' }}>
                    <WeekdayStrip yearMonth={yearMonth} sx={{ mb: 0.5 }} />
                    <DaySquares daySheet={daySheet} yearMonth={yearMonth} />
                </Box>
            </Box>
        </Box>
    );
};

/* ------------------------------------------------------------------ */
/* List                                                                */
/* ------------------------------------------------------------------ */
const UsersTimesheet = () => {
    const { data: users, year, month } = useMonthlyTimesheetsState();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const yearMonth = `${year}-${month}`;

    if (users.length === 0) return <TimesheetsNoDataFound />;

    if (isMobile) {
        return (
            <Box sx={{ px: 0.5 }}>
                {users.map((user, idx) => (
                    <UserCard
                        key={user.email || idx}
                        user={user}
                        yearMonth={yearMonth}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer sx={{ boxShadow: 'none' }}>
                <TableHeaderContainer>
                    <TableCellContainer
                        typography='caption'
                        sx={{
                            ...stickySx(3),
                            pl: 2,
                            fontWeight: 'bold',
                            minWidth: 230
                        }}
                    >
                        Employee
                    </TableCellContainer>

                    {SUMMARY_KEYS.map((key) => (
                        <TableCellContainer
                            key={key}
                            typography='caption'
                            sx={{
                                fontWeight: 'bold',
                                textAlign: 'center',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Tooltip
                                title={
                                    timesheetSummaryHeaderFullNames[key] || ''
                                }
                            >
                                <span style={{ cursor: 'help' }}>
                                    {timesheetSummaryHeaders[key]}
                                </span>
                            </Tooltip>
                        </TableCellContainer>
                    ))}

                    <TableCellContainer typography='caption'>
                        <WeekdayStrip yearMonth={yearMonth} />
                    </TableCellContainer>

                    <TableCellContainer
                        typography='caption'
                        sx={{
                            fontWeight: 'bold',
                            textAlign: 'center',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Hours
                    </TableCellContainer>
                </TableHeaderContainer>
                <TableBody>
                    {users.map((user, idx) => (
                        <UserRow
                            key={user.email || idx}
                            user={user}
                            yearMonth={yearMonth}
                        />
                    ))}
                </TableBody>
            </TableContainer>
        </Box>
    );
};

export default UsersTimesheet;
